// `Log` over a LogTape logger. Plugins see the `Log` interface from
// @chess/api-log; LogTape is this plugin's business only.
import { getLogger, type Logger, type LogRecord as LogTapeRecord } from '@logtape/logtape';
import { errorFields, type Fields, type Log, type LogLevel, type LogRecord } from '@chess/api-log';

export class LogTapeLog implements Log {
  private readonly logger: Logger;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  static for(category: string[]): LogTapeLog {
    return new LogTapeLog(getLogger(category));
  }

  child(category: string): Log {
    return new LogTapeLog(this.logger.getChild(category));
  }

  debug(message: string, fields?: Fields): void {
    this.logger.debug(message, fields ?? {});
  }

  info(message: string, fields?: Fields): void {
    this.logger.info(message, fields ?? {});
  }

  warn(message: string, fields?: Fields): void {
    this.logger.warn(message, fields ?? {});
  }

  error(message: string, fields?: Fields | Error): void {
    this.logger.error(message, errorFields(fields));
  }
}

const levels: Record<string, LogLevel> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warning: 'warn',
  error: 'error',
  fatal: 'error',
};

/** A LogTape record in the shape the `log` contract and any sink of ours expects. */
export function toRecord(record: LogTapeRecord): LogRecord {
  return {
    level: levels[record.level] ?? 'info',
    category: [...record.category],
    message: typeof record.rawMessage === 'string' ? record.rawMessage : record.message.map(String).join(''),
    fields: { ...record.properties },
    timestamp: record.timestamp,
  };
}
